import { callFirebaseFunction } from 'boot/firebase';
import RequestSignature from 'components/Sessions/RequestSignature';
import Signature from 'components/Signature';
import { getDownloadURL } from 'firebase/storage';
import { chain, get } from 'lodash';
import { uid, useQuasar } from 'quasar';
import { notifyNegative, useDialog } from 'src/composables/interactions';
import { useSignature } from 'src/composables/signature';
import AddendumsDB from 'src/fb/addendums-db';
import { auth as useAuthStore } from 'src/store/auth';
import { geolocation as useGeolocationStore } from 'src/store/geolocation';
import { network as useNetworkStore } from 'src/store/network';
import { logEvent } from 'src/utils/LogEvent';
import { shallowRef, watch } from 'vue';
import { useCollection } from 'vuefire';

/**
 * Addendum list binding and the signature/delete actions for a session note.
 *
 * `uploadSignature` is passed in because it still lives on the
 * `SessionNotesTemplates` mixin, which the note's own signature flow also uses.
 *
 * @param {Object} params
 * @param {import('vue').Ref<Object>} params.sessionActivity
 * @param {import('vue').Ref<Object|null>} params.session
 * @param {import('vue').Ref<Object|null>} params.client
 * @param {() => Object|undefined} params.getSessionNoteTemplate
 * @param {(signature: string, path: string, sessionId: string|null, cb: Function) => Promise<void>}
 *   params.uploadSignature
 */
// eslint-disable-next-line import/prefer-default-export
export function useAddendums({
  sessionActivity,
  session,
  client,
  getSessionNoteTemplate,
  uploadSignature,
}) {
  const $q = useQuasar();
  const authStore = useAuthStore();
  const geolocationStore = useGeolocationStore();
  const networkStore = useNetworkStore();
  const { openComponentDialog } = useDialog();
  const { getStaffInfoForSignature } = useSignature();

  const addendumsQuery = shallowRef(null);

  // A null query binds nothing, which is how addendums stay unloaded offline.
  // The guard has to come first: offline, `getQuery` returns an IndexedDB ref
  // that vuefire cannot bind.
  watch(
    () => [sessionActivity.value?.id, networkStore.offlineMode],
    ([sessionActivityId, isOffline]) => {
      if (!sessionActivityId || isOffline) {
        addendumsQuery.value = null;
        return;
      }

      addendumsQuery.value = new AddendumsDB(sessionActivityId)
        .getQuery([['isDeleted', '==', false]], 'createTimestamp', 'asc');
    },
    { immediate: true },
  );

  const {
    data: addendums,
    pending: isLoadingAddendums,
    error: addendumsLoadError,
  } = useCollection(addendumsQuery);

  const includeProfessionalCredentials = () => {
    return getSessionNoteTemplate()?.includeProfessionalCredentials;
  };

  const withLoading = async (action) => {
    try {
      $q.loading.show();
      await action();
    } catch (err) {
      notifyNegative(err.message);
      logEvent(err);
    } finally {
      $q.loading.hide();
    }
  };

  const signAddendum = async (addendum, signature, signatoryUid = null) => {
    await callFirebaseFunction('addAddendumSignature', {
      sessionActivityId: sessionActivity.value.id,
      addendumId: addendum.id,
      signature,
      signatoryUid,
    });
  };

  const saveSignature = async (addendum, signatureData, signatureDetails, signatoryUid) => {
    const { clinic, id } = sessionActivity.value;
    const destinationPath = `/clinics/${clinic}/sessionActivities/${id}/addendums/${addendum.id}/signatures/`;

    await uploadSignature(
      signatureData,
      destinationPath,
      sessionActivity.value.session || null,
      async (signatureRef) => withLoading(async () => {
        const url = await getDownloadURL(signatureRef);
        await signAddendum(addendum, { ...signatureDetails, url }, signatoryUid);
      }),
    );
  };

  const showSignatureDialog = (addendum, { anonymous, userId, staffInfo, label = '', signatoryUid = null }) => {
    geolocationStore.refreshGeolocation(false);

    openComponentDialog(
      Signature,
      {
        anonymous,
        userName: staffInfo.userName,
        includeGeolocation: true,
        dialogTitle: label,
        userId,
      },
      async (signatureInfo) => {
        const signatureDetails = {
          userName: signatureInfo.userName || null,
          userId: signatureInfo.signatory?.userId || userId,
          label: signatureInfo.signatory?.label || '',
          ...(signatureInfo.geolocation && { geolocation: signatureInfo.geolocation }),
          ...(staffInfo.professionalCredentials && {
            professionalCredentials: staffInfo.professionalCredentials,
          }),
        };

        await saveSignature(addendum, signatureInfo.data, signatureDetails, signatoryUid);
      },
    );
  };

  const resolveStaffInfo = async (userName, userId) => {
    try {
      $q.loading.show();
      return await getStaffInfoForSignature(userName, userId, includeProfessionalCredentials());
    } finally {
      $q.loading.hide();
    }
  };

  const requestSignature = (addendum) => {
    const skipUsers = chain(addendum)
      .get('signatures')
      .map('userId')
      .concat(chain(addendum).get('requiredSignatories', []).map('userId').value())
      .concat([authStore.userDetails.userId])
      .compact()
      .value();

    openComponentDialog(
      RequestSignature,
      {
        sessionId: get(session.value, 'id', null),
        clientId: get(client.value, 'id', null),
        skipUsers,
      },
      async ({ selectedUser }) => {
        if (!selectedUser) {
          return;
        }

        await withLoading(async () => {
          const staffInfo = await getStaffInfoForSignature(
            selectedUser.userName,
            selectedUser.user,
            includeProfessionalCredentials(),
          );

          await callFirebaseFunction('requestAddendumSignature', {
            sessionActivityId: sessionActivity.value.id,
            addendumId: addendum.id,
            signatory: {
              uid: uid(),
              userId: selectedUser.user,
              ...staffInfo,
              label: '',
              completed: false,
            },
          });
        });
      },
    );
  };

  const signRequest = async (addendum, signatory) => {
    const staffInfo = await resolveStaffInfo(signatory.userName, signatory.userId);

    showSignatureDialog(addendum, {
      anonymous: signatory.userId === null,
      userId: signatory.userId,
      staffInfo,
      label: signatory.label,
      signatoryUid: signatory.uid,
    });
  };

  const signNow = async (addendum) => {
    const { name, userId } = authStore.userDetails;
    const staffInfo = await resolveStaffInfo(name, userId);

    showSignatureDialog(addendum, { anonymous: false, userId, staffInfo });
  };

  const addSignature = (addendum) => {
    showSignatureDialog(addendum, {
      anonymous: true,
      userId: null,
      staffInfo: { userName: null },
    });
  };

  const cancelSignatureRequest = (addendum, signatory) => withLoading(async () => {
    await callFirebaseFunction('cancelAddendumSignatureRequest', {
      sessionActivityId: sessionActivity.value.id,
      addendumId: addendum.id,
      signatoryUid: signatory.uid,
    });
  });

  const deleteAddendum = (addendum) => withLoading(async () => {
    await callFirebaseFunction('deleteAddendum', {
      sessionActivityId: sessionActivity.value.id,
      addendumId: addendum.id,
    });
  });

  return {
    addendums,
    isLoadingAddendums,
    addendumsLoadError,
    requestSignature,
    signRequest,
    signNow,
    addSignature,
    cancelSignatureRequest,
    deleteAddendum,
  };
}
